/**
 * DeleteBRRButton Component
 * Button with confirmation dialog for deleting an in-progress BRR.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Trash2, AlertTriangle, X } from 'lucide-react';

interface DeleteBRRButtonProps {
  onDelete: () => Promise<void>;
  disabled?: boolean;
}

export const DeleteBRRButton: React.FC<DeleteBRRButtonProps> = ({
  onDelete,
  disabled = false,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDeleteClick = () => {
    setShowConfirm(true);
    setError(null);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setError(null);
  };

  // A successful onDelete navigates away, which unmounts this button before the
  // promise resolves - the state updates below only apply while still mounted.
  const handleConfirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      if (isMountedRef.current) setShowConfirm(false);
    } catch (err) {
      if (isMountedRef.current) setError(err instanceof Error ? err.message : 'Failed to delete BRR');
    } finally {
      if (isMountedRef.current) setDeleting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleDeleteClick}
        disabled={disabled}
        className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Trash2 size={16} />
        Delete BRR
      </button>

      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 bg-red-50 border-b border-red-100">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-red-900">Delete BRR?</h3>
              <button
                onClick={handleCancel}
                className="ml-auto p-1 hover:bg-red-100 rounded transition-colors"
                disabled={deleting}
              >
                <X size={20} className="text-red-600" />
              </button>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete this BRR? All data related to this BRR will be permanently deleted, including:
              </p>
              <ul className="mt-3 ml-4 text-sm text-gray-600 list-disc space-y-1">
                <li>Document validation data</li>
                <li>Form field values and overrides</li>
                <li>Uploaded evidence documents</li>
                <li>Session progress</li>
              </ul>
              <p className="mt-4 text-sm font-medium text-red-700">
                This action cannot be undone.
              </p>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={handleCancel}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete BRR
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
